<template>
  <div id="app">
    <!-- Global Custom Cursor -->
    <div class="custom-cursor" ref="cursor"></div>
    
    <LoadingSpinner 
      :isLoading="isLoading" 
      :loadingText="loadingText"
      :showProgress="showProgress"
      :minLoadTime="minLoadTime"
      @loading-complete="onLoadingComplete"
    />
    <AppHeader v-if="!$route.meta.hideHeaderFooter" />
    <router-view/>
    <AppFooter v-if="!$route.meta.hideHeaderFooter" />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    LoadingSpinner
  },
  data() {
    return {
      isLoading: true,
      loadingText: 'Loading...',
      showProgress: true,
      minLoadTime: 3000, // 全局最少3秒loading時間
      imageLoadingComplete: false,
      initialLoadComplete: false
    }
  },
      mounted() {
      // 更智能的loading控制
      this.initLoading();
      
      // Initialize global custom cursor
      this.initCustomCursor();
      
      // 監聽窗口大小變化以重新檢測移動設備
      window.addEventListener('resize', this.handleResize);
      
      // 監聽路由變化以調整loading
      this.$router.afterEach(this.handleRouteChange);
    },
  methods: {
    initLoading() {
      // 基於當前路由調整loading設定
      const currentRoute = this.$route.path;
      this.updateLoadingForRoute(currentRoute);
      
      // 預加載關鍵圖片
      this.preloadImages();
      
      // 多重檢查機制
      window.addEventListener('load', this.checkCompleteLoad);
      if (document.readyState === 'complete') {
        this.checkCompleteLoad();
      }
      
      // 安全網 - 最大loading時間
      setTimeout(() => {
        this.forceCompleteLoading();
      }, 8000);
    },
    
    updateLoadingForRoute(routePath) {
      if (routePath.includes('/project')) {
        this.loadingText = 'Loading Project Gallery...';
        this.minLoadTime = 6000; // 專案頁面需要更多時間
      } else if (routePath.includes('/artists')) {
        this.loadingText = 'Loading Artists Information...';
        this.minLoadTime = 5500;
      } else if (routePath.includes('/virtual-exhibition')) {
        this.loadingText = 'Loading...';
        this.minLoadTime = 8000; // 虛擬展覽需要最長時間
      } else if (routePath === '/') {
        this.loadingText = 'Loading @all Exhibition...';
        this.minLoadTime = 5000;
      } else {
        this.loadingText = 'Loading...';
        this.minLoadTime = 4000;
      }
    },
    
    preloadImages() {
      // 預加載首頁重要圖片
      const criticalImages = [
        '/fingerprint.svg',
        // 可以根據需要添加更多關鍵圖片
      ];
      
      let loadedCount = 0;
      const totalImages = criticalImages.length;
      
      if (totalImages === 0) {
        this.imageLoadingComplete = true;
        return;
      }
      
      criticalImages.forEach(src => {
        const img = new Image();
        img.onload = img.onerror = () => {
          loadedCount++;
          if (loadedCount === totalImages) {
            this.imageLoadingComplete = true;
            this.checkCompleteLoad();
          }
        };
        img.src = src;
      });
    },
    
    checkCompleteLoad() {
      if (!this.initialLoadComplete) {
        this.initialLoadComplete = true;
        // 檢查是否所有條件都滿足
        if (this.imageLoadingComplete) {
          this.hideLoader();
        } else {
          // 如果圖片還沒加載完成，再等一下
          setTimeout(() => {
            this.hideLoader();
          }, 1000);
        }
      }
    },
    
    forceCompleteLoading() {
      if (this.isLoading) {
        console.log('Force completing loading after timeout');
        this.hideLoader();
      }
    },
    
    handleRouteChange(to) {
      // 在路由變化時顯示loading
      if (this.shouldShowLoadingForRoute(to.path)) {
        this.isLoading = true;
        this.updateLoadingForRoute(to.path);
        
        // 為新頁面設置適當的延遲
        setTimeout(() => {
          this.hideLoader();
        }, this.minLoadTime * 0.6); // 路由切換時縮短loading時間
      }
    },
    
    shouldShowLoadingForRoute(path) {
      // 定義哪些路由切換需要loading
      const heavyRoutes = ['/project', '/artists', '/virtual-exhibition'];
      return heavyRoutes.some(route => path.includes(route));
    },
    
    hideLoader() {
      this.isLoading = false;
    },
    
    onLoadingComplete() {
      // 從LoadingSpinner組件接收完成事件
      this.isLoading = false;
    },
    
    isMobileDevice() {
      // 檢測移動設備的多種方法
      const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const smallScreen = window.innerWidth <= 768;
      
      // 綜合判斷：用戶代理包含移動設備標識 或 支持觸控 或 螢幕寬度小於768px
      return mobileUA || (touchDevice && smallScreen);
    },
    
    handleResize() {
      // 當窗口大小改變時重新初始化游標
      this.initCustomCursor();
    },
    
    initCustomCursor() {
      // 檢測是否為移動設備
      const isMobile = this.isMobileDevice();
      const cursor = this.$refs.cursor;
      
      if (isMobile) {
        // 移動設備：隱藏自定義游標，恢復默認游標
        cursor.style.display = 'none';
        document.body.style.cursor = 'auto';
        return;
      }
      
      // 桌面設備：啟用自定義游標
      cursor.style.display = 'block';
      document.body.style.cursor = 'none';
      
      // Mouse move handler with smooth animation
      this.mouseMoveHandler = (e) => {
        requestAnimationFrame(() => {
          cursor.style.left = e.clientX + 'px';
          cursor.style.top = e.clientY + 'px';
        });
      };
      
      // Mouse enter handler for interactive elements
      this.mouseEnterHandler = () => {
        cursor.classList.add('cursor-small');
      };
      
      // Mouse leave handler for interactive elements
      this.mouseLeaveHandler = () => {
        cursor.classList.remove('cursor-small');
      };
      
      // Add event listeners
      document.addEventListener('mousemove', this.mouseMoveHandler);
      
      // Add hover effects for interactive elements
      this.addHoverEffects();
    },
    
    addHoverEffects() {
      const interactiveSelectors = 'a, button, .exhibition-frame, .info-card, .router-link-active, input, textarea, select, [role="button"], .clickable';
      
      const updateInteractiveElements = () => {
        // Remove old listeners
        if (this.interactiveElements) {
          this.interactiveElements.forEach(element => {
            element.removeEventListener('mouseenter', this.mouseEnterHandler);
            element.removeEventListener('mouseleave', this.mouseLeaveHandler);
          });
        }
        
        // Add new listeners
        const elements = document.querySelectorAll(interactiveSelectors);
        elements.forEach(element => {
          element.addEventListener('mouseenter', this.mouseEnterHandler);
          element.addEventListener('mouseleave', this.mouseLeaveHandler);
        });
        
        this.interactiveElements = elements;
      };
      
      // Initial setup
      updateInteractiveElements();
      
      // Update on route change
      this.$router.afterEach(() => {
        setTimeout(updateInteractiveElements, 100);
      });
    },
    
    removeCustomCursor() {
      if (this.mouseMoveHandler) {
        document.removeEventListener('mousemove', this.mouseMoveHandler);
      }
      
      if (this.interactiveElements) {
        this.interactiveElements.forEach(element => {
          element.removeEventListener('mouseenter', this.mouseEnterHandler);
          element.removeEventListener('mouseleave', this.mouseLeaveHandler);
        });
      }
      
      // Restore default cursor
      document.body.style.cursor = 'auto';
    }
  },
  beforeDestroy() {
    window.removeEventListener('load', this.hideLoader);
    window.removeEventListener('resize', this.handleResize);
    this.removeCustomCursor();
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  background-color: transparent;
  cursor: none;
}

body {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  line-height: 1.6;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  width: 100%;
  overflow-x: hidden;
}

.main-content {
  flex: 1;
}

a {
  text-decoration: none;
  color: #42b983;
  transition: color 0.3s;
}

a:hover {
  color: #2c3e50;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

h1, h2, h3, h4, h5, h6 {
  margin-bottom: 20px;
}

p {
  margin-bottom: 16px;
}

/* Global Custom Cursor Styles */
.custom-cursor {
  position: fixed;
  top: 0;
  left: 0;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, rgba(240, 131, 30, 0.8) 0%, rgba(0, 77, 107, 0.6) 70%, transparent 100%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 99999;
  transform: translate(-50%, -50%);
  transition: width 0.15s ease, height 0.15s ease, filter 0.15s ease;
  filter: blur(1px);
  mix-blend-mode: difference;
  will-change: transform;
}

.custom-cursor.cursor-small {
  width: 50px;
  height: 50px;
  filter: blur(0.5px);
}

/* Hide cursor on all elements globally (only on desktop) */
@media (hover: hover) and (pointer: fine) {
  * {
    cursor: none !important;
  }
}

/* Ensure mobile devices keep default cursor */
@media (hover: none) or (pointer: coarse) {
  * {
    cursor: auto !important;
  }
  
  .custom-cursor {
    display: none !important;
  }
}
</style>
